/**
 * CampusGround
 *
 * Modern tech campus environment with concrete ground, walkways,
 * landscaped green areas, and decorative elements.
 */

import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { CampusLocation } from '../../../types/spatial';
import { CAMPUS_LAYOUT } from '../../../config/locationConfig';

// Building positions for path routing (excluding campus overview)
const BUILDING_TARGETS = [
  CAMPUS_LAYOUT[CampusLocation.DESIGN_STUDIO],
  CAMPUS_LAYOUT[CampusLocation.RD_LAB],
  CAMPUS_LAYOUT[CampusLocation.MANUFACTURING],
  CAMPUS_LAYOUT[CampusLocation.SALES_OFFICE],
  CAMPUS_LAYOUT[CampusLocation.EXECUTIVE_OFFICE],
  CAMPUS_LAYOUT[CampusLocation.COMPANY_ARCHIVES],
];

// Tree placement positions (hand-tuned to avoid buildings and paths)
const TREE_POSITIONS: [number, number, number][] = [
  [-12, 0, 0], [-14, 0, 4], [-14, 0, -5],
  [12, 0, 0], [14, 0, -4], [13, 0, 5],
  [-8, 0, -18], [8, 0, -18], [-6, 0, -22],
  [7, 0, 16], [-7, 0, 16],
  [-28, 0, 8], [28, 0, -8], [-25, 0, -15],
  [25, 0, 15], [0, 0, 28],
];

// Green patch areas (position, radius)
const GREEN_PATCHES: { pos: [number, number, number]; radius: number }[] = [
  { pos: [-12, 0.01, 0], radius: 6 },
  { pos: [12, 0.01, 0], radius: 6 },
  { pos: [0, 0.01, -18], radius: 5 },
  { pos: [6, 0.01, 16], radius: 4 },
  { pos: [-6, 0.01, 16], radius: 4 },
  { pos: [-26, 0.01, 0], radius: 5 },
  { pos: [26, 0.01, 0], radius: 5 },
];

// Lamp positions along paths
const LAMP_POSITIONS: [number, number, number][] = [
  [-8, 0, 5], [8, 0, 5], [-8, 0, -5], [8, 0, -5],
  [0, 0, 10], [0, 0, -12],
  [-15, 0, 8], [15, 0, -8],
];

/** Low-poly tree with gentle canopy sway */
function Tree({ position }: { position: [number, number, number] }) {
  const canopyRef = useRef<THREE.Group>(null);
  // Each tree sways at a slightly different rate based on position
  const phase = position[0] * 0.3 + position[2] * 0.5;

  useFrame(({ clock }) => {
    if (canopyRef.current) {
      const t = clock.getElapsedTime();
      canopyRef.current.rotation.z = Math.sin(t * 0.5 + phase) * 0.03;
      canopyRef.current.rotation.x = Math.cos(t * 0.4 + phase) * 0.02;
    }
  });

  return (
    <group position={position}>
      <mesh position={[0, 1.2, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.18, 2.4, 6]} />
        <meshStandardMaterial color="#4a3728" roughness={0.9} metalness={0} />
      </mesh>
      <group ref={canopyRef}>
        <mesh position={[0, 3, 0]} castShadow>
          <coneGeometry args={[1.4, 3, 6]} />
          <meshStandardMaterial color="#2d5a27" roughness={0.85} metalness={0} />
        </mesh>
        <mesh position={[0, 3.8, 0]} castShadow>
          <coneGeometry args={[1, 2, 6]} />
          <meshStandardMaterial color="#357a2e" roughness={0.85} metalness={0} />
        </mesh>
      </group>
    </group>
  );
}

/** Lamp post with subtle warm light variation */
function LampPost({ position }: { position: [number, number, number] }) {
  const lightRef = useRef<THREE.PointLight>(null);
  const phase = position[0] * 0.7 + position[2] * 0.3;

  useFrame(({ clock }) => {
    if (lightRef.current) {
      const t = clock.getElapsedTime();
      lightRef.current.intensity = 0.28 + Math.sin(t * 1.5 + phase) * 0.04;
    }
  });

  return (
    <group position={position}>
      <mesh position={[0, 1.5, 0]}>
        <cylinderGeometry args={[0.04, 0.06, 3, 8]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[0, 3.1, 0]}>
        <sphereGeometry args={[0.18, 8, 8]} />
        <meshStandardMaterial
          color="#ffeebb"
          emissive="#ffcc66"
          emissiveIntensity={0.6}
        />
      </mesh>
      <pointLight ref={lightRef} position={[0, 3.1, 0]} color="#ffcc66" intensity={0.3} distance={8} />
    </group>
  );
}

/** Walkway path from center to a building */
function Walkway({ target }: { target: { x: number; z: number } }) {
  const length = Math.sqrt(target.x ** 2 + target.z ** 2);
  const angle = Math.atan2(target.x, target.z);
  if (length < 1) return null;

  return (
    <mesh
      position={[target.x / 2, 0.02, target.z / 2]}
      rotation={[0, angle, 0]}
      receiveShadow
    >
      <boxGeometry args={[1.8, 0.04, length]} />
      <meshStandardMaterial color="#5c6166" roughness={0.85} metalness={0.05} />
    </mesh>
  );
}

export const CampusGround: React.FC = () => {
  return (
    <group>
      {/* Main ground -- large concrete circle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <circleGeometry args={[48, 64]} />
        <meshStandardMaterial color="#4a4f54" roughness={0.92} metalness={0.05} />
      </mesh>

      {/* Outer ring / curb */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <ringGeometry args={[46, 48, 64]} />
        <meshStandardMaterial color="#3a3f44" roughness={0.9} metalness={0.1} />
      </mesh>

      {/* Central plaza -- lighter concrete circle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <circleGeometry args={[7, 32]} />
        <meshStandardMaterial color="#5a6066" roughness={0.85} metalness={0.05} />
      </mesh>

      {/* Central plaza accent ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <ringGeometry args={[6.5, 7, 32]} />
        <meshStandardMaterial color="#667077" roughness={0.8} metalness={0.1} />
      </mesh>

      {/* Walkways from center to each building */}
      {BUILDING_TARGETS.map((target, i) => (
        <Walkway key={i} target={target} />
      ))}

      {/* Green landscape patches */}
      {GREEN_PATCHES.map((patch, i) => (
        <mesh
          key={`grass-${i}`}
          rotation={[-Math.PI / 2, 0, 0]}
          position={patch.pos}
          receiveShadow
        >
          <circleGeometry args={[patch.radius, 24]} />
          <meshStandardMaterial color="#2d5a27" roughness={0.95} metalness={0} />
        </mesh>
      ))}

      {/* Trees */}
      {TREE_POSITIONS.map((pos, i) => (
        <Tree key={`tree-${i}`} position={pos} />
      ))}

      {/* Lamp posts */}
      {LAMP_POSITIONS.map((pos, i) => (
        <LampPost key={`lamp-${i}`} position={pos} />
      ))}

      {/* Surrounding terrain (beyond campus edge) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]} receiveShadow>
        <circleGeometry args={[120, 64]} />
        <meshStandardMaterial color="#3a4a3e" roughness={0.95} metalness={0} />
      </mesh>
    </group>
  );
};

export default CampusGround;
